import {ListItem, useListViewVariant} from '@github-ui/list-view'
import {CommentIcon} from '@primer/octicons-react'
import {Box, Text} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import {Assignees} from './assignees/Assignees'
import {ClosedByPullRequestsReferences} from './closed_by_pull_requests_references/ClosedByPullRequestsReferences'
import type {IssueItemMetadata$key} from './__generated__/IssueItemMetadata.graphql'
import {QUERY_FIELDS} from '../constants/queries'
import {Reactions} from './Reactions'
import {TEST_IDS} from '../constants/test-ids'

export type IssueItemMetadataProps = {
  itemKey: IssueItemMetadata$key
  showAssignees?: boolean
  showCommentCount?: boolean
  showCommentZeroCount?: boolean
  showLinkedPullRequests?: boolean
  /**
   * Href getter for the metadata badge links
   * @param type - metadata type
   * @param name - name of the metadata
   * @returns URL to the metadata
   */
  getMetadataHref: (type: keyof typeof QUERY_FIELDS, name: string) => string
  reactionEmojiToDisplay?: {reaction: string; reactionEmoji: string}
}

const IssueItemMetadataQuery = graphql`
  fragment IssueItemMetadata on Issue
  @argumentDefinitions(
    assigneePageSize: {type: "Int!", defaultValue: 10}
    closedByPullRequestsReferencesPageSize: {type: "Int!", defaultValue: 10}
    includeReactions: {type: "Boolean!", defaultValue: false}
  ) {
    # Used to check if reactions were loaded
    reactionGroups @include(if: $includeReactions) {
      __typename
    }
    ... on Reactable @include(if: $includeReactions) {
      ...Reactions @include(if: $includeReactions)
    }
    totalCommentsCount
    ...Assignees @arguments(assigneePageSize: $assigneePageSize)
    ...ClosedByPullRequestsReferences
      @arguments(closedByPullRequestsReferencesPageSize: $closedByPullRequestsReferencesPageSize)
  }
`

export const IssueItemMetadata = ({
  itemKey,
  getMetadataHref,
  reactionEmojiToDisplay,
  showAssignees,
  showCommentCount = true,
  showCommentZeroCount = false,
  showLinkedPullRequests = false,
}: IssueItemMetadataProps) => {
  const {variant} = useListViewVariant()
  const data = useFragment(IssueItemMetadataQuery, itemKey)

  const commentAriaLabel =
    data?.totalCommentsCount && data.totalCommentsCount > 0
      ? ` ${data.totalCommentsCount} comment${data.totalCommentsCount > 1 ? 's' : ''};`
      : ''
  const showReactions = !!reactionEmojiToDisplay?.reaction
  const showCommentCountMetadata =
    (showCommentCount && !!data.totalCommentsCount) || (showCommentCount && showCommentZeroCount)
  const commentCount = showCommentCountMetadata && data.totalCommentsCount ? data.totalCommentsCount : 0

  return (
    <>
      {data && showLinkedPullRequests && (
        <ListItem.Metadata sx={{width: ['auto', '45px']}} data-testid={TEST_IDS.listRowLinkedPullRequests}>
          <ClosedByPullRequestsReferences closedByPullRequestsReferencesKey={data} />
        </ListItem.Metadata>
      )}
      <ListItem.Metadata
        sx={{width: ['auto', '45px']}}
        aria-label={commentAriaLabel}
        data-testid={TEST_IDS.listRowComments}
      >
        {showCommentCountMetadata && (
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <>
              <CommentIcon size={16} /> <Text sx={{ml: 1}}>{commentCount}</Text>
              <span className="sr-only">{commentCount === 1 ? ' comment' : ' comments'}</span>
            </>
          </Box>
        )}
      </ListItem.Metadata>
      {showReactions && (
        <ListItem.Metadata sx={{width: ['auto', '45px']}}>
          {data.reactionGroups && (
            <Reactions
              dataKey={data}
              reactionEmojiToDisplay={reactionEmojiToDisplay}
              showCompactDensity={variant === 'compact'}
            />
          )}
        </ListItem.Metadata>
      )}
      {showAssignees && data && (
        <ListItem.Metadata sx={{width: ['auto', '45px']}} data-testid={TEST_IDS.listRowAssignees} alignment="right">
          <Assignees
            assigneeskey={data}
            getAssigneeHref={assignee => getMetadataHref(QUERY_FIELDS.assignee, assignee)}
          />
        </ListItem.Metadata>
      )}
    </>
  )
}

try{ IssueItemMetadata.displayName ||= 'IssueItemMetadata' } catch {}